import * as XLSX from "xlsx";

export const HEADER_SCAN_ROWS = 25;

export function normalizeText(value) {
  return String(value ?? "")
    .replace(/\r/g, " ")
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeHeader(value) {
  return normalizeText(value).toLowerCase();
}

export function findSheetName(sheetNames, target) {
  const targetKey = normalizeHeader(target);
  return sheetNames.find((name) => normalizeHeader(name) === targetKey) || null;
}

export function createHeaders(row = []) {
  const seen = new Map();

  return row.map((value, index) => {
    const base = normalizeHeader(value) || `column_${index + 1}`;
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}_${count + 1}`;
  });
}

export function getRawRows(sheet, blankRows = false) {
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: false,
    blankrows: blankRows,
  });
}

export function getSheetRows(sheet, headerRowIndex = 0, blankRows = false) {
  if (!sheet) return [];

  const rawRows = getRawRows(sheet, blankRows);

  const headerRow = rawRows[headerRowIndex] || [];
  const headers = createHeaders(headerRow);

  return rawRows
    .slice(headerRowIndex + 1)
    .map((row) => {
      const record = {};
      headers.forEach((header, index) => {
        record[header] = row[index] ?? "";
      });
      return record;
    })
    .filter((row) =>
      Object.values(row).some((value) => normalizeText(value) !== "")
    );
}

export function findHeaderRow(sheet, requiredHeaders, maxScan = HEADER_SCAN_ROWS) {
  const rawRows = getRawRows(sheet, true).slice(0, maxScan);

  for (let index = 0; index < rawRows.length; index += 1) {
    const headers = createHeaders(rawRows[index] || []);
    if (requiredHeaders.every((header) => headers.includes(header))) {
      return index + 1;
    }
  }
  return 0;
}

export function sheetRowOffset(sheet) {
  try {
    return XLSX.utils.decode_range(sheet["!ref"]).s.r;
  } catch (error) {
    return 0;
  }
}

export function parseDateToken(value) {
  if (!value && value !== 0) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const text = normalizeText(value);
  if (!text) return null;

  const isoMatch = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (isoMatch) {
    const [, year, month, day, hour = "0", minute = "0", second = "0"] = isoMatch;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );
  }

  const auMatch = text.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/
  );
  if (auMatch) {
    let [, day, month, year, hour = "0", minute = "0", second = "0", meridiem] = auMatch;
    let parsedHour = Number(hour);

    if (meridiem) {
      const upper = meridiem.toUpperCase();
      if (upper === "PM" && parsedHour < 12) parsedHour += 12;
      if (upper === "AM" && parsedHour === 12) parsedHour = 0;
    }

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      parsedHour,
      Number(minute),
      Number(second)
    );
  }

  const fallback = new Date(text);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function pad(value) {
  return String(value).padStart(2, "0");
}

export function formatDateValue(value) {
  const date = parseDateToken(value);
  if (!date) return "";

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
